import type { ExplorerTypeId } from "./explorerViews.constants"

/**
 * What a condition compares. `values`: one of a set of names (a carrier, a
 * state). `date`: a moment, compared relative to today. `flag`: yes or no.
 */
type ExplorerConditionKind = "values" | "date" | "flag"

/** Where a field sits in the picker. */
export type ExplorerConditionGroup = "load" | "places" | "dates" | "paperwork"

interface ExplorerConditionFieldDef {
    kind: ExplorerConditionKind
    group: ExplorerConditionGroup
    /** The typed Typesense field the condition filters on. */
    typesenseField: string
    /** For a date: the bool field that says whether the date is set at all. */
    presenceField?: string
    /** Still matched so a view saved with an older value keeps its records. */
    previousValueField?: string
    types: readonly ExplorerTypeId[]
}

/**
 * Every field a custom view can filter on, per record type. The server
 * builds Typesense clauses from these and the client builds the picker from
 * them, so a field only exists once. Values come from typed Typesense fields
 * filled at index time (`extractTypesenseFilterFields`).
 */
export const EXPLORER_CONDITION_FIELDS = {
    status: {
        kind: "values",
        group: "load",
        typesenseField: "status",
        types: [
            "shipments",
            "salesOrders",
            "quotes",
            "products",
            "vendors",
            "addressBook",
        ],
    },
    carrier: {
        kind: "values",
        group: "load",
        typesenseField: "carrierFacet",
        types: ["shipments"],
    },
    mode: {
        kind: "values",
        group: "load",
        typesenseField: "modeFacet",
        types: ["shipments"],
    },
    shipper: {
        kind: "values",
        group: "places",
        typesenseField: "shipperFacet",
        previousValueField: "shipperPersonFacet",
        types: ["shipments"],
    },
    consignee: {
        kind: "values",
        group: "places",
        typesenseField: "consigneeFacet",
        previousValueField: "consigneePersonFacet",
        types: ["shipments"],
    },
    /** Values are user ids; the picker shows each user's name. */
    assignee: {
        kind: "values",
        group: "places",
        typesenseField: "assigneeIds",
        types: ["shipments"],
    },
    originState: {
        kind: "values",
        group: "places",
        typesenseField: "originState",
        types: ["shipments"],
    },
    originCity: {
        kind: "values",
        group: "places",
        typesenseField: "originCity",
        types: ["shipments"],
    },
    destinationState: {
        kind: "values",
        group: "places",
        typesenseField: "destinationState",
        types: ["shipments"],
    },
    destinationCity: {
        kind: "values",
        group: "places",
        typesenseField: "destinationCity",
        types: ["shipments"],
    },
    pickup: {
        kind: "date",
        group: "dates",
        typesenseField: "pickupAtMs",
        presenceField: "hasPickupDate",
        types: ["shipments"],
    },
    delivery: {
        kind: "date",
        group: "dates",
        typesenseField: "deliveryDueAtMs",
        presenceField: "hasDeliveryDate",
        types: ["shipments"],
    },
    bol: {
        kind: "flag",
        group: "paperwork",
        typesenseField: "hasBol",
        types: ["shipments"],
    },
    pod: {
        kind: "flag",
        group: "paperwork",
        typesenseField: "hasPod",
        types: ["shipments"],
    },
    invoice: {
        kind: "flag",
        group: "paperwork",
        typesenseField: "hasInvoice",
        types: ["shipments"],
    },
    requestedDelivery: {
        kind: "date",
        group: "dates",
        typesenseField: "requestedDeliveryAtMs",
        types: ["salesOrders"],
    },
    belowReorderPoint: {
        kind: "flag",
        group: "load",
        typesenseField: "belowReorderPoint",
        types: ["products"],
    },
    vendorKind: {
        kind: "values",
        group: "load",
        typesenseField: "vendorKind",
        types: ["vendors"],
    },
    insuranceExpiry: {
        kind: "date",
        group: "dates",
        typesenseField: "insuranceExpiresAtMs",
        types: ["vendors"],
    },
    locationKind: {
        kind: "values",
        group: "places",
        typesenseField: "locationKind",
        types: ["addressBook"],
    },
    appointmentRequired: {
        kind: "flag",
        group: "places",
        typesenseField: "appointmentRequired",
        types: ["addressBook"],
    },
} as const satisfies Record<string, ExplorerConditionFieldDef>

export type ExplorerConditionField = keyof typeof EXPLORER_CONDITION_FIELDS

export const EXPLORER_CONDITION_FIELD_IDS = Object.keys(
    EXPLORER_CONDITION_FIELDS
) as [ExplorerConditionField, ...ExplorerConditionField[]]

export const EXPLORER_CONDITION_GROUPS: readonly ExplorerConditionGroup[] = [
    "load",
    "places",
    "dates",
    "paperwork",
]

/** The fields a record type's views can filter on, in picker order. */
export const explorerConditionFieldsForType = (
    typeId: ExplorerTypeId
): ExplorerConditionField[] =>
    EXPLORER_CONDITION_FIELD_IDS.filter(field => {
        const types: readonly ExplorerTypeId[] =
            EXPLORER_CONDITION_FIELDS[field].types
        return types.includes(typeId)
    })

/** Date comparisons, all relative to the day the view is opened. */
export const EXPLORER_DATE_OPS = [
    "today",
    "beforeToday",
    "withinNextDays",
    "thisWeek",
    "notSet",
] as const
export type ExplorerDateOp = (typeof EXPLORER_DATE_OPS)[number]

export const EXPLORER_CONDITION_MAX_VALUES = 25

export const EXPLORER_CONDITION_MAX_COUNT = 12
